import { useEffect, useState } from 'react';

const API = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL);

/**
 * Generic authenticated poller. Fires immediately and then every `intervalMs`.
 * `enabled=false` returns 0 and does not hit the network.
 * `extract` maps the JSON payload to a numeric count.
 */
function usePollingCount({ url, intervalMs, enabled, extract }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const fetchOnce = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}${url}`, {
          // `no-store` makes the browser bypass its HTTP cache entirely for
          // these polls. Without it, a stale cached 301/308 redirect (e.g.
          // one cached during a DNS/redirect outage) can send the request
          // cross-origin to the platform host and fail CORS forever.
          cache: 'no-store',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setCount(extract(data) || 0);
      } catch {
        // silent — badges are non-critical
      }
    };

    fetchOnce();
    const handle = setInterval(fetchOnce, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [url, intervalMs, enabled, extract]);

  return count;
}

const pickUnreadCount = (d) => d.unread_count;
const pickTotalUnread = (d) => d.total_unread;

/**
 * Aggregate all sidebar badge counts for the current role.
 * Returns three numbers: parent notifications, team-chat unread, WhatsApp inbox unread.
 */
export function useDashboardBadges({ role, whatsappEnabled = true }) {
  const isParent = role === 'parent';
  const isAdmin = role === 'admin';
  const isCoach = role === 'coach';
  const isStaff = role === 'staff';

  const notificationCount = usePollingCount({
    url: '/api/notifications/count',
    intervalMs: 60_000,
    enabled: isParent,
    extract: pickUnreadCount,
  });

  const chatUnreadCount = usePollingCount({
    url: '/api/chat/unread-count',
    intervalMs: 30_000,
    enabled: isParent || isCoach || isAdmin || isStaff,
    extract: pickUnreadCount,
  });

  const whatsappUnread = usePollingCount({
    url: '/api/whatsapp/threads?status=active&limit=200',
    intervalMs: 30_000,
    enabled: isAdmin && whatsappEnabled,
    extract: pickTotalUnread,
  });

  return { notificationCount, chatUnreadCount, whatsappUnread };
}
